// Top common/compromised passwords — compact shortlist.
// Source: well-known public breach compilations (rockyou + have-i-been-pwned top lists).
// Stored lowercase; compare after .toLowerCase() on user input.
export const COMMON_PASSWORDS = new Set([
  '123456', '123456789', '12345678', '1234567', '1234567890', '12345', '123123',
  'password', 'password1', 'password123', 'passw0rd', 'pass1234', 'pass123',
  'qwerty', 'qwerty123', 'qwertyuiop', 'azerty', 'azerty123', 'azertyuiop',
  'abc123', 'abcd1234', 'abcdef', 'abc12345', 'a1b2c3', 'a1b2c3d4',
  'iloveyou', 'iloveu', 'motdepasse', 'motdepasse1', 'motdepasse123', 'secret',
  'admin', 'admin123', 'administrator', 'root', 'toor', 'letmein', 'welcome',
  'welcome1', 'welcome123', 'monkey', 'dragon', 'superman', 'batman', 'master',
  'sunshine', 'princess', 'shadow', 'football', 'baseball', 'soccer', 'hockey',
  'starwars', 'pokemon', 'naruto', 'computer', 'internet', 'samsung', 'apple',
  'google', 'facebook', 'linkedin', 'instagram', 'twitter', 'snapchat',
  'bonjour', 'bonjour1', 'coucou', 'salut', 'france', 'paris', 'marseille',
  'lyon', 'bordeaux', 'toulouse', 'nice', 'nantes', 'strasbourg',
  'bienvenue', 'chocolat', 'chocolate', 'chien', 'chat', 'famille',
  'anniversaire', 'vacances', 'soleil', 'amour', 'maison',
  '0000', '00000', '000000', '1111', '11111', '111111', '112233', '123321',
  '1q2w3e', '1q2w3e4r', '1qaz2wsx', 'zaq12wsx', 'qazwsx', 'qwe123', 'qweasd',
  'trustno1', 'whatever', 'nothing', 'somebody', 'passwd', 'passwort', 'mypass',
  'test', 'test123', 'test1234', 'test1', 'demo', 'demo123', 'guest', 'guest123',
  'user', 'user123', 'login', 'login123', 'changeme', 'changeme123',
  'michelle', 'jennifer', 'nicole', 'jessica', 'ashley', 'daniel', 'michael',
  'charlie', 'thomas', 'robert', 'william', 'richard', 'matthew', 'andrew',
  'anthony', 'donald', 'steven', 'joseph', 'george', 'david',
  'martin', 'dupont', 'durand', 'bernard', 'petit', 'robert', 'leroy',
  'moreau', 'simon', 'laurent', 'lefevre',
  'manager', 'boss', 'hello', 'hello123', 'hello1234',
  '87654321', '987654321', 'qwer1234', 'qwertz', 'asdfghjkl', 'asdf1234',
  'letmein123', 'passw0rd1', 'zxcvbnm', 'zxcvbn', 'qscwdv', 'tyty',
  'ilove', 'iloveme', 'iloveyou1', 'iloveyou2',
  'football1', 'baseball1', 'hockey1',
  'newyork', 'london', 'tokyo', 'berlin', 'madrid', 'rome',
  'summer', 'winter', 'spring', 'autumn', 'january', 'february', 'march',
  'june', 'july', 'august', 'september', 'october', 'november', 'december',
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  'freedom', 'liberty', 'america', 'american',
  'star', 'stars', 'cool', 'cool123',
  'secret1', 'secret123', 'killer', 'killer123',
  'cheese', 'pepper', 'silver', 'golden',
  'access', 'access123', 'passe', 'passe123', 'passer', 'passeword',
  'changement', 'azerty1', 'azerty12',
]);

export function isCommonPassword(pw) {
  if (!pw) return false;
  return COMMON_PASSWORDS.has(pw.toLowerCase());
}

/**
 * Score a password from 0 (weak) to 4 (strong).
 * Returns { score, label, color, criteria: [{key, label, met}], common }
 */
export function scorePassword(pw) {
  const p = pw || '';
  const criteria = [
    { key: 'length', label: 'Au moins 8 caractères', met: p.length >= 8 },
    { key: 'lower', label: 'Une lettre minuscule', met: /[a-z]/.test(p) },
    { key: 'upper', label: 'Une lettre majuscule', met: /[A-Z]/.test(p) },
    { key: 'digit', label: 'Un chiffre', met: /[0-9]/.test(p) },
    { key: 'special', label: 'Un caractère spécial', met: /[^A-Za-z0-9]/.test(p) },
  ];
  const met = criteria.filter((c) => c.met).length;
  const common = isCommonPassword(p);

  // Base score: 1 point per criterion (max 5). Cap at 4 for display.
  let score = Math.min(met, 4);
  // Extra bonus for longer passwords
  if (p.length >= 12 && met >= 4) score = 4;
  // Penalty for common passwords, whatever else
  if (common) score = Math.min(score, 1);
  if (p.length === 0) score = 0;

  const labels = ['Très faible', 'Faible', 'Moyen', 'Bon', 'Excellent'];
  const colors = ['#EF4444', '#F59E0B', '#F59E0B', '#22C55E', '#14B8A6'];

  return {
    score,
    label: p.length === 0 ? '' : labels[score],
    color: colors[score],
    criteria,
    common,
  };
}
